import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Tabs,
	Tab,
	Typography,
	Card,
	CardContent,
	Button,
	TextField,
	Divider,
	IconButton,
	Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { useSelection } from '../../hooks/use-selection';
import { GenericTable, Column } from '../../components/generic/generic.table';
import EditLocation from '../../components/generic/edit.location';
import { LIST_ALL_SHOPS, UPDATE_SHOP, DELETE_LOCATION } from '../../gql/queries';
import { DATE_FORMAT } from '../../constants';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`shop-tabpanel-${index}`}
			aria-labelledby={`shop-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
		</div>
	);
}

interface Location {
	_id: string;
	name: string;
	address: {
		city: string;
		street?: string;
		state?: string;
		zipcode?: string;
	};
	email: string;
	phone: string;
}

const locationColumns: Column[] = [
	{ title: 'Location Name', key: 'name' },
	{
		title: 'City',
		key: 'address',
		formatter: (column: Column, data: Location) => {
			return data.address?.city || 'N/A';
		}
	},
	{
		title: 'Address',
		key: 'address',
		formatter: (column: Column, data: Location) => {
			const addr = data.address;
			return addr?.street ? `${addr.street}, ${addr.state || ''} ${addr.zipcode || ''}`.trim() : 'N/A';
		}
	},
	{ title: 'Phone', key: 'phone' },
	{ title: 'Email', key: 'email' },
];

export default function ShopDetail() {
	const { id: shopId } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState(0);
	const [isEditing, setIsEditing] = useState(false);
	const [shopName, setShopName] = useState('');

	const { data, loading, refetch } = useQuery(LIST_ALL_SHOPS);
	const [updateShop, { loading: updating }] = useMutation(UPDATE_SHOP);
	const [deleteLocation] = useMutation(DELETE_LOCATION);

	// Find current shop
	const currentShop = data?.shops?.find((shop: any) => shop._id === shopId);
	const locations = currentShop?.location || [];

	const locationsSelection = useSelection(locations);

	// Initialize shop name when data loads
	useEffect(() => {
		if (currentShop?.name && !shopName) {
			setShopName(currentShop.name);
		}
	}, [currentShop]);

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleBack = () => {
		navigate('/dashboard/shops');
	};

	const handleEditToggle = () => {
		setIsEditing(!isEditing);
		if (isEditing) {
			// Cancel editing - reset to original
			setShopName(currentShop?.name || '');
		}
	};

	const handleSaveShop = useCallback(async () => {
		if (!shopName.trim()) {
			toast.error('Shop name is required');
			return;
		}

		try {
			await updateShop({
				variables: {
					shopId: shopId,
					shop: {
						name: shopName.trim(),
						location: []
					}
				}
			});
			toast.success('Shop updated successfully');
			setIsEditing(false);
			await refetch();
		} catch (error: any) {
			toast.error(`Failed to update shop: ${error.message}`);
		}
	}, [shopId, shopName, updateShop, refetch]);

	const handleDeleteLocation = useCallback(
		async (location: Location) => {
			if (!window.confirm(`Are you sure you want to delete "${location.name}"? This action cannot be undone.`)) {
				return;
			}

			try {
				await deleteLocation({
					variables: {
						shopId: shopId,
						locationId: location._id
					}
				});
				toast.success(`Deleted ${location.name} successfully.`);
				await refetch();
			} catch (error: any) {
				toast.error(`Failed to delete location: ${error.message}`);
			}
		},
		[deleteLocation, shopId, refetch]
	);

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
				<Typography>Loading...</Typography>
			</Box>
		);
	}

	if (!currentShop) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
				<Typography>Shop not found</Typography>
			</Box>
		);
	}

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				py: 8
			}}
		>
			<Container maxWidth="xl">
				{/* Header */}
				<Box sx={{ mb: 3 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
						<IconButton onClick={handleBack} size="small">
							<ArrowBackIcon />
						</IconButton>
						<Typography variant="h4">
							{currentShop.name}
						</Typography>
						<Chip 
							label={`${locations.length} Location${locations.length !== 1 ? 's' : ''}`} 
							color="primary" 
							size="small" 
						/>
					</Box>
					<Typography variant="body2" color="text.secondary">
						Manage shop details and locations
					</Typography>
				</Box>

				{/* Tabs */}
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<Tabs value={currentTab} onChange={handleTabChange} aria-label="shop tabs">
						<Tab label="Basic Details" id="shop-tab-0" aria-controls="shop-tabpanel-0" />
						<Tab label={`Locations (${locations.length})`} id="shop-tab-1" aria-controls="shop-tabpanel-1" />
					</Tabs>
				</Box>

				{/* Tab 1: Basic Details */}
				<TabPanel value={currentTab} index={0}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
								<Typography variant="h6">Shop Information</Typography>
								<Box sx={{ display: 'flex', gap: 2 }}>
									{isEditing ? (
										<>
											<Button
												variant="outlined"
												onClick={handleEditToggle}
												disabled={updating}
											>
												Cancel
											</Button>
											<Button
												variant="contained"
												onClick={handleSaveShop}
												disabled={updating}
											>
												{updating ? 'Saving...' : 'Save Changes'}
											</Button>
										</>
									) : (
										<Button
											variant="contained"
											onClick={handleEditToggle}
										>
											Edit Shop
										</Button>
									)}
								</Box>
							</Box>

							<Divider sx={{ mb: 3 }} />

							<Box sx={{ maxWidth: 600 }}>
								<TextField
									fullWidth
									label="Shop Name"
									value={shopName || currentShop.name}
									onChange={(e) => setShopName(e.target.value)}
									disabled={!isEditing}
									sx={{ mb: 3 }}
								/>

								<Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
									<Typography variant="body2" color="text.secondary">Created:</Typography>
									<Typography variant="body2">
										{currentShop.createdAt ? format(new Date(currentShop.createdAt), DATE_FORMAT) : 'N/A'}
									</Typography>

									<Typography variant="body2" color="text.secondary">Last Updated:</Typography>
									<Typography variant="body2">
										{currentShop.updatedAt ? format(new Date(currentShop.updatedAt), DATE_FORMAT) : 'N/A'}
									</Typography>

									<Typography variant="body2" color="text.secondary">Total Locations:</Typography>
									<Typography variant="body2">{locations.length}</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</TabPanel>

				{/* Tab 2: Locations */}
				<TabPanel value={currentTab} index={1}>
					<GenericTable
						title="Locations"
						data={locations}
						columns={locationColumns}
						onDeselectAll={locationsSelection.handleDeselectAll}
						onDeselectOne={locationsSelection.handleDeselectOne}
						onSelectAll={locationsSelection.handleSelectAll}
						onSelectOne={locationsSelection.handleSelectOne}
						onDeleteOne={handleDeleteLocation}
						selected={locationsSelection.selected}
						addEditComponent={EditLocation}
						showSelectCheckBox={false}
						refetch={refetch}
						loading={loading}
					/>
				</TabPanel>
			</Container>
		</Box>
	);
}
